import {isEqual} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {appRoute} from '../../../../app-route';
import {CursorPaginator} from '../../../../component/cursor-paginator/cursor-paginator';
import {Meta} from '../../../../component/meta/meta';
import {ActivationPage} from '../../../../layout/activation-page/activation-page';
import {Page} from '../../../../layout/page/page';
import {MainPageContainer} from '../../../../layout/page-card/main-page-card';
import {TableControlPanel} from '../../../../layout/table/table-control-panel/table-control-panel';
import {useDocumentationLinks} from '../../../../provider/help-links/help-links-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {useSystem} from '../../../../provider/system/system-hook';
import {ReviewCatalogType} from '../../../../service/reviews/reviews-catalogs';
import {ReviewSortingTypeEnum} from '../../../../service/reviews/reviews-type';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {useSelectedItems} from '../../../../util/selected-items/use-selected-items';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {reviewPageSizeOptions} from '../reviews-const';
import {ReviewsHeader} from '../reviews-header/reviews-header';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from '../reviews-state/reviews-state-type';

import {NeuralNetworkAlert} from './neuralink-alert/neuralink-alert';
import {NewReviewList} from './review-list/new-review-list';
import {BulkAbuseModal} from './review-list/review/review-header/bulk-abuse-modal/bulk-abuse-modal';
import {BulkAnswerModal} from './review-list/review/review-header/bulk-answer-modal/bulk-answer-modal';
import {ResponsibleUserModal} from './review-list/review/review-header/responsible-user-modal/responsible-user-modal';
import {StatusChangeModal} from './review-list/review/review-header/status-change-modal/status-change-modal';
import {ReviewDataWithCatalogType} from './review-list/review-list-type';
import {ReviewsFilter} from './reviews-filter/reviews-filter';
import {ReviewsFilterDataType} from './reviews-filter/reviews-filter-type';
import * as styles from './review-list-page-content.scss';

// eslint-disable-next-line complexity, max-statements
export function ReviewListPageContent(): JSX.Element {
    const [isFilterOpened, setIsFilterOpened] = useState(true);
    const [isSelectMode, setIsSelectMode] = useState(false);
    const {
        reviews,
        cursorPagination,
        isReviewsInProgress,
        updateReview,
        refreshReview,
        reviewsState,
        reviewsCatalogList,
    } = useReviewStateHook();
    const {getLocalizedString} = useLocale();
    const {licenses} = useLicenses();
    const systemContext = useSystem();
    const {screen} = systemContext;
    const {isMobile} = screen;
    const {featureActivation} = useDocumentationLinks();
    const {replaceQuery: setReviewsStateUrlQuery, queries} = useUrl<
        Omit<ReviewsStateType, 'filterState'> & ReviewsFilterDataType
    >();

    const [isOpenResponsibleUserModal, setIsOpenResponsibleUserModal] = useState<boolean>(false);
    const [isOpenStatusChangeModal, setIsOpenStatusChangeModal] = useState<boolean>(false);
    const [isOpenBulkAnswerModal, setIsOpenBulkAnswerModal] = useState<boolean>(false);
    const [isOpenBulkAbuseModal, setIsOpenBulkAbuseModal] = useState<boolean>(false);

    const hasReviews = useMemo(() => Boolean(reviews?.results.length), [reviews]);
    const total = reviews?.results.length ?? 0;

    const {selectedCount, isRowSelected, deselectAll, toggleRow, clear, selectedKeys, isInverted} =
        useSelectedItems<ReviewDataWithCatalogType>(total);

    const toggleSelectMode = useCallback(() => {
        setIsSelectMode((previous) => !previous);

        if (isSelectMode) {
            clear();
        }
    }, [isSelectMode, clear]);

    const handleCancelSelect = useCallback(() => {
        deselectAll();
        setIsSelectMode(false);
    }, [deselectAll]);

    const handleBulkAction = useCallback(() => {
        setIsOpenResponsibleUserModal(true);
    }, []);

    const handleCloseBulkModal = useCallback(() => {
        setIsSelectMode(false);
        clear();
    }, [clear]);

    const isReviewSelected = useCallback((review: ReviewDataWithCatalogType) => isRowSelected(review), [isRowSelected]);

    const toggleReviewSelection = useCallback((review: ReviewDataWithCatalogType) => toggleRow(review), [toggleRow]);

    const newFilterComponent = useMemo(
        () => (
            <ReviewsHeader
                hasReviews={hasReviews}
                isSelectMode={isSelectMode}
                onToggleSelectMode={toggleSelectMode}
                setIsFilterOpened={setIsFilterOpened}
            />
        ),
        [isSelectMode, toggleSelectMode, hasReviews]
    );

    useEffect(() => {
        // eslint-disable-next-line no-undefined
        if (queries.ordering === undefined) {
            setReviewsStateUrlQuery({
                [ReviewsStateUrlQueryNameEnum.sorting]: ReviewSortingTypeEnum.acs,
            });
        }
    }, [queries.ordering]);

    useEffect(() => {
        setIsFilterOpened(!isMobile);
    }, [isMobile]);

    const previousFilterStateRef = useRef(reviewsState?.filterState);

    useEffect(() => {
        const currentFilterState = reviewsState?.filterState;
        const previousFilterState = previousFilterStateRef.current;

        if (previousFilterState && currentFilterState && !isEqual(previousFilterState, currentFilterState)) {
            clear();
        }

        previousFilterStateRef.current = currentFilterState;
    }, [clear, reviewsState?.filterState]);

    const catalogListMap = useMemo(() => {
        if (!reviewsCatalogList) {
            return {};
        }

        return reviewsCatalogList.reduce((accumulator, current) => {
            return {
                ...accumulator,
                [current.id]: current,
            };
        }, {} as Record<number, ReviewCatalogType>);
    }, [reviewsCatalogList]);

    const allSelectedReviews = useMemo(() => {
        const fullObjects = selectedKeys.filter(
            (key): key is ReviewDataWithCatalogType => typeof key === 'object' && key !== null && 'id' in key
        );

        if (isInverted) {
            if (!reviews?.results) {
                return [];
            }

            const reviewsWithCatalog: Array<ReviewDataWithCatalogType> = reviews.results.map((review) => ({
                ...review,
                catalog: catalogListMap[review.catalogId] || null,
            }));

            const excludedIds = new Set(fullObjects.map((key) => key.id));

            return reviewsWithCatalog.filter((review) => !excludedIds.has(review.id));
        }

        return fullObjects;
    }, [isInverted, selectedKeys, reviews?.results, catalogListMap]);

    const currentPageReviews = useMemo(() => {
        if (!reviews?.results) {
            return [];
        }

        return reviews.results.map((review) => ({
            ...review,
            catalog: catalogListMap[review.catalogId] || null,
        }));
    }, [reviews?.results, catalogListMap]);

    const allCurrentPageSelected = useMemo(() => {
        if (currentPageReviews.length === 0) {
            return false;
        }

        return currentPageReviews.every((review) => isRowSelected(review));
    }, [currentPageReviews, isRowSelected]);

    const toggleAllCurrentPage = useCallback(() => {
        if (currentPageReviews.length === 0) {
            return;
        }

        if (allCurrentPageSelected) {
            currentPageReviews.forEach((review) => {
                if (isRowSelected(review)) {
                    toggleRow(review);
                }
            });
        } else {
            currentPageReviews.forEach((review) => {
                if (!isRowSelected(review)) {
                    toggleRow(review);
                }
            });
        }
    }, [currentPageReviews, allCurrentPageSelected, isRowSelected, toggleRow]);

    if (!licenses[FeaturesEnum.reviews]?.isActive) {
        return (
            <ActivationPage
                breadCrumbList={[
                    {
                        path: appRoute.reviewsManagement.path,
                        titleLangKey: 'CATEGORY_NAME__REVIEWS',
                    },
                ]}
                interestedMessageLangKey="ACTIVATION_PAGE__INTERESTED_CONTACT_YOUR_MANAGER_TO_ACTIVATE_THIS_SERVICE"
                link={featureActivation[FeaturesEnum.reviews]}
                title={<Locale stringKey="CATEGORY_NAME__REVIEWS" />}
            />
        );
    }

    // eslint-disable-next-line no-undefined
    const firstSelectedReview = allSelectedReviews.length > 0 ? allSelectedReviews[0] : undefined;

    return (
        <Page renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <Meta title={getLocalizedString('CATEGORY_NAME__REVIEWS')} />
            {newFilterComponent}
            <NeuralNetworkAlert />
            <div className={styles.Reviews_contentWrapper}>
                <div className={styles.Reviews_list}>
                    <NewReviewList
                        isRowSelected={isReviewSelected}
                        isSelectMode={isSelectMode}
                        toggleRow={toggleReviewSelection}
                    />
                    {hasReviews && !isReviewsInProgress && (
                        <CursorPaginator cursorPagination={cursorPagination} pageSizeOptions={reviewPageSizeOptions} />
                    )}
                </div>

                <div className={styles.Reviews_filter} hidden={!isFilterOpened}>
                    <ReviewsFilter />
                </div>
            </div>
            {total > 0 && (
                <TableControlPanel
                    disableSelectedCount
                    fourthButtonConfig={{
                        isLoading: false,
                        langKey: 'REVIEWS__COMPLAINT_POPUP__SUBMITTING_A_COMPLAINT',
                        onClick: () => setIsOpenBulkAbuseModal(true),
                    }}
                    isVisible={isSelectMode && total > 0}
                    onCancel={handleCancelSelect}
                    secondaryButtonConfig={{
                        isLoading: false,
                        isDashed: false,
                        langKey: 'REVIEWS__FILTER__SELECT_A_STATUS',
                        onClick: () => setIsOpenStatusChangeModal(true),
                    }}
                    selectedCount={selectedCount}
                    selectedCountLabel="CATEGORY_NAME__REVIEWS"
                    submitButtonConfig={{
                        isLoading: false,
                        langKey: 'REVIEWS__REPLY',
                        onClick: () => setIsOpenBulkAnswerModal(true),
                    }}
                    thirdButtonConfig={{
                        isLoading: false,
                        langKey: 'REVIEW__DROPDOWN_MENU__APPOINT_RESPONSIBLE',
                        onClick: handleBulkAction,
                    }}
                    toggleAllButtonConfig={{
                        isLoading: isReviewsInProgress,
                        langKey: allCurrentPageSelected ? 'TEXT__CANCEL_SELECT' : 'TEXT__SELECT_ALL',
                        onClick: toggleAllCurrentPage,
                    }}
                    totalCount={currentPageReviews.length}
                />
            )}
            {isOpenResponsibleUserModal && firstSelectedReview && (
                <ResponsibleUserModal
                    onCancel={() => setIsOpenResponsibleUserModal(false)}
                    onSuccess={() => {
                        setIsOpenResponsibleUserModal(false);
                        handleCloseBulkModal();
                    }}
                    reviewData={firstSelectedReview}
                    reviewId={firstSelectedReview.id}
                    selectedReviews={allSelectedReviews}
                    updateReview={updateReview}
                />
            )}
            {isOpenStatusChangeModal && allSelectedReviews.length > 0 && (
                <StatusChangeModal
                    onCancel={() => setIsOpenStatusChangeModal(false)}
                    onSuccess={() => {
                        setIsOpenStatusChangeModal(false);
                        handleCloseBulkModal();
                    }}
                    selectedReviews={allSelectedReviews}
                    updateReview={updateReview}
                />
            )}
            {isOpenBulkAnswerModal && allSelectedReviews.length > 0 && (
                <BulkAnswerModal
                    onCancel={() => setIsOpenBulkAnswerModal(false)}
                    onSuccess={() => {
                        setIsOpenBulkAnswerModal(false);
                        handleCloseBulkModal();
                    }}
                    refreshReview={refreshReview}
                    selectedReviews={allSelectedReviews}
                    updateReview={updateReview}
                />
            )}
            {isOpenBulkAbuseModal && allSelectedReviews.length > 0 && (
                <BulkAbuseModal
                    onCancel={() => setIsOpenBulkAbuseModal(false)}
                    onSuccess={() => {
                        setIsOpenBulkAbuseModal(false);
                        handleCloseBulkModal();
                    }}
                    refreshReview={refreshReview}
                    selectedReviews={allSelectedReviews}
                    updateReview={updateReview}
                />
            )}
        </Page>
    );
}
